import { registerSW } from 'virtual:pwa-register';

/**
 * One owner for update detection and for the worker registration.
 *
 * Two things are deliberate here.
 *
 * Detection asks the server directly rather than waiting for the service worker
 * to notice. The proxy rewrites the cache headers on sw.js, and a browser only
 * re-checks a worker on its own schedule, so an app left open could sit on an
 * old build indefinitely. /api/version is uncacheable and authoritative.
 *
 * The dismissal is stored against the version it dismissed. Module state alone
 * was lost on reload, and a waiting worker re-announces itself on every check,
 * which is how the prompt came back over and over.
 */

const DISMISSED_KEY = 'ponyup-dismissed-version';
const POLL_INTERVAL_MS = 60_000;
const POLL_DEBOUNCE_MS = 15_000;

interface State {
    /** What the server says is deployed, once known. */
    serverVersion: string | null;
    dismissedVersion: string | null;
    checking: boolean;
    lastCheckedAt: Date | null;
}

const readDismissed = (): string | null => {
    try {
        return localStorage.getItem(DISMISSED_KEY);
    } catch {
        return null;
    }
};

let state: State = {
    serverVersion: null,
    dismissedVersion: readDismissed(),
    checking: false,
    lastCheckedAt: null,
};

let registration: ServiceWorkerRegistration | null = null;
let lastPollAt = 0;
let intervalId: ReturnType<typeof setInterval> | null = null;

const listeners = new Set<() => void>();

const set = (patch: Partial<State>): void => {
    state = { ...state, ...patch };
    listeners.forEach((listener) => listener());
};

const applyViaServiceWorker = registerSW({
    immediate: true,
    onRegisteredSW(_url, reg) {
        registration = reg ?? null;
        startWatching();
    },
});

const poll = async (manual: boolean): Promise<void> => {
    // A resume can fire visibilitychange, focus and pageshow together.
    const now = Date.now();
    if (!manual && now - lastPollAt < POLL_DEBOUNCE_MS) return;
    if (!navigator.onLine) return;
    lastPollAt = now;

    if (manual) set({ checking: true });
    try {
        const response = await fetch('/api/version', { cache: 'no-store' });
        if (!response.ok) return;
        const { version } = (await response.json()) as { version?: string };
        if (typeof version !== 'string') return;

        set({ serverVersion: version });

        // Pull the new worker down so applying it is instant, and so the new
        // assets are cached before the reload rather than during it.
        if (version !== __APP_COMMIT__ && registration) {
            void registration.update().catch(() => undefined);
        }
    } catch {
        // Offline or a blip; the next poll retries.
    } finally {
        if (manual) set({ checking: false });
        set({ lastCheckedAt: new Date() });
    }
};

const startWatching = (): void => {
    if (intervalId !== null) return;

    intervalId = setInterval(() => void poll(false), POLL_INTERVAL_MS);

    const onResume = (): void => {
        if (document.visibilityState === 'visible') void poll(false);
    };

    window.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);
    window.addEventListener('pageshow', onResume);
    window.addEventListener('online', onResume);

    void poll(true);
};

export const subscribeToAppUpdate = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const getAppUpdateState = (): State => state;

/**
 * A newer build is deployed than the one running.
 *
 * Refuses to decide when either side is unknown. A stamp can fail to be
 * injected -- it silently did once, when the deploy script's build argument was
 * not reaching the image -- and comparing against a placeholder would report an
 * update forever, with every reload landing back on the same answer.
 */
export const isUpdateWaiting = (): boolean => {
    const server = state.serverVersion;
    if (server === null || server === 'unknown' || __APP_COMMIT__ === 'unknown') return false;
    return server !== __APP_COMMIT__;
};

/**
 * Loads the new build.
 *
 * Prefers handing over to the waiting worker, which swaps the cached assets
 * cleanly. Falls back to a plain reload: index.html is served uncached, so it
 * comes back pointing at the new hashed bundles either way.
 */
export const applyAppUpdate = (): void => {
    void (async () => {
        try {
            await applyViaServiceWorker(true);
        } catch {
            window.location.reload();
        }
        // If the worker handover did not navigate within a moment, force it.
        setTimeout(() => window.location.reload(), 1500);
    })();
};

/** Stops asking about this particular version, across reloads. */
export const dismissAppUpdate = (): void => {
    const version = state.serverVersion;
    if (!version) return;
    try {
        localStorage.setItem(DISMISSED_KEY, version);
    } catch {
        // A blocked store just means it asks again next load.
    }
    set({ dismissedVersion: version });
};

export const checkForAppUpdate = (): void => void poll(true);

export const appVersion = (): string => `${__APP_COMMIT__} · ${__APP_BUILT_AT__}`;

export const appCommit = (): string => __APP_COMMIT__;
